import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { MapPin, Calendar, Clock, Users, ArrowRight, Luggage, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../lib/api';
import HybridLocationInput from './HybridLocationInput';
import { 
  isValidTime, 
  isValidDate, 
  sanitizeTimeInput, 
  sanitizeDateInput,
  getSafeTimeValue,
  getSafeDateValue 
} from '../utils/timeUtils';

const BookingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tripType, setTripType] = useState('oneway');
  const [isLoading, setIsLoading] = useState(false);
  const [airports, setAirports] = useState([]);
  const dropoffRef = useRef(null);
  const autocompleteRef = useRef(null);
  const googleLoadedRef = useRef(false);

  // Transfer Mode State
  const [transferData, setTransferData] = useState({
    pickup: '',
    pickupLat: null,
    pickupLng: null,
    pickupType: null, // 'airport', 'custom', 'manual'
    pickupAirportCode: null,
    dropoff: '',
    dropoffLat: null,
    dropoffLng: null,
    date: '',
    returnDate: '',
    time: '',
    returnTime: '',
    passengers: '1',
    luggage: '1',
  });

  // Load airports from API
  useEffect(() => {
    const fetchAirports = async () => {
      const data = await apiGet('/api/airports');
      if (data && data.success && data.airports) {
        setAirports(data.airports);
      }
    };
    fetchAirports();
  }, []);

  // Check for prefill from Popular Routes
  useEffect(() => {
    const prefillPickup = searchParams.get('pickup');
    const prefillDropoff = searchParams.get('dropoff');
    const prefillPickupLat = searchParams.get('pickupLat');
    const prefillPickupLng = searchParams.get('pickupLng');
    const prefillDropoffLat = searchParams.get('dropoffLat');
    const prefillDropoffLng = searchParams.get('dropoffLng');

    if (prefillPickup || prefillDropoff) {
      // Try to match pickup to an airport in the dropdown
      let matchedAirport = null;
      if (prefillPickup && airports.length > 0) {
        // Check if pickup matches any airport name or code
        const lowerPickup = prefillPickup.toLowerCase();
        matchedAirport = airports.find(a => 
          lowerPickup.includes(a.code?.toLowerCase()) ||
          lowerPickup.includes(a.name?.toLowerCase()) ||
          a.fullName?.toLowerCase().includes(lowerPickup) ||
          a.value?.toLowerCase().includes(lowerPickup)
        );
      }

      setTransferData(prev => ({
        ...prev,
        pickup: matchedAirport ? (matchedAirport.fullName || matchedAirport.value) : (prefillPickup || prev.pickup),
        pickupType: matchedAirport ? 'airport' : (prefillPickupLat ? 'custom' : 'manual'),
        pickupAirportCode: matchedAirport ? matchedAirport.code : null,
        pickupLat: prefillPickupLat ? parseFloat(prefillPickupLat) : (matchedAirport?.lat || prev.pickupLat),
        pickupLng: prefillPickupLng ? parseFloat(prefillPickupLng) : (matchedAirport?.lng || prev.pickupLng),
        dropoff: prefillDropoff || prev.dropoff,
        dropoffLat: prefillDropoffLat ? parseFloat(prefillDropoffLat) : prev.dropoffLat,
        dropoffLng: prefillDropoffLng ? parseFloat(prefillDropoffLng) : prev.dropoffLng,
      }));
    }
  }, [searchParams, airports]);

  // Initialize Google Places Autocomplete
  const initAutocomplete = useCallback(() => {
    if (!dropoffRef.current || !window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    // Prevent double initialization
    if (autocompleteRef.current) {
      return;
    }

    try {
      // Define bounds for Turkey + Northern Cyprus
      const turkeyAndCyprusBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(34.5, 25.5),  // Southwest (includes Cyprus)
        new window.google.maps.LatLng(42.5, 45.0)   // Northeast (Turkey)
      );
      
      autocompleteRef.current = new window.google.maps.places.Autocomplete(dropoffRef.current, {
        bounds: turkeyAndCyprusBounds,
        strictBounds: false,  // Allow results outside bounds but prioritize inside
        fields: ['formatted_address', 'geometry', 'name', 'place_id'],
        types: ['establishment', 'geocode']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry && place.geometry.location) {
          setTransferData(prev => ({
            ...prev,
            dropoff: place.formatted_address || place.name || '',
            dropoffLat: place.geometry.location.lat(),
            dropoffLng: place.geometry.location.lng(),
          }));
        }
      });
      
      console.log('Google Places Autocomplete initialized successfully');
    } catch (err) {
      console.error('Google Places Autocomplete init error:', err);
    }
  }, []);

  // Cleanup Google Autocomplete dropdown elements to prevent removeChild errors
  useEffect(() => {
    return () => {
      // Clean up pac-container elements created by Google Autocomplete
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        if (container.parentNode) {
          try {
            container.parentNode.removeChild(container);
          } catch (e) {
            // Ignore removal errors
          }
        }
      });
    };
  }, []);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = () => {
      // Check if already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        googleLoadedRef.current = true;
        initAutocomplete();
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          googleLoadedRef.current = true;
          initAutocomplete();
        });
        return;
      }

      // Create and load script
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.warn('Google Maps API key not found');
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Google Maps API loaded');
        googleLoadedRef.current = true;
        initAutocomplete();
      };
      
      script.onerror = (err) => {
        console.error('Failed to load Google Maps API:', err);
      };

      document.head.appendChild(script);
    };

    loadGoogleMaps();

    // Cleanup
    return () => {
      if (autocompleteRef.current && window.google) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        } catch (e) {}
      }
      // Clean up pac-container elements
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        try {
          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        } catch (e) {}
      });
    };
  }, [initAutocomplete]);

  // Re-init autocomplete when dropoff ref changes
  useEffect(() => {
    if (googleLoadedRef.current && dropoffRef.current && !autocompleteRef.current) {
      initAutocomplete();
    }
  }, [initAutocomplete]);

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    
    // Safe handling for time inputs
    if (name === 'time' || name === 'returnTime') {
      const safeTime = sanitizeTimeInput(value);
      setTransferData((prev) => ({
        ...prev,
        [name]: safeTime,
      }));
      return;
    }
    
    // Safe handling for date inputs
    if (name === 'date' || name === 'returnDate') {
      const safeDate = sanitizeDateInput(value);
      setTransferData((prev) => ({
        ...prev,
        [name]: safeDate,
      }));
      return;
    }
    
    setTransferData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'passengers') {
        newData.luggage = value;
      }
      return newData;
    });
  };

  // Handle hybrid pickup location change
  const handlePickupChange = (locationData) => {
    setTransferData(prev => ({
      ...prev,
      pickup: locationData.value,
      pickupLat: locationData.lat,
      pickupLng: locationData.lng,
      pickupType: locationData.type,
      pickupAirportCode: locationData.airportCode,
    }));
  };

  const handleDropoffInputChange = (e) => {
    const value = e.target.value;
    setTransferData(prev => ({
      ...prev,
      dropoff: value,
      // Clear coords when manually typing - will be set by autocomplete or backend
      dropoffLat: null,
      dropoffLng: null,
    }));
  };

  const handleLuggageChange = (delta) => {
    setTransferData((prev) => ({
      ...prev,
      luggage: Math.max(0, Math.min(50, parseInt(prev.luggage) + delta)).toString(),
    }));
  };

  const validateTransfer = () => {
    // 8.1 Frontend Validation - Pickup field validation
    
    // Check if pickup field is empty
    if (!transferData.pickup || transferData.pickup.trim().length < 3) {
      toast.error('Please select a pickup location', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // Check if pickup type is set (ensures normalized state exists)
    // Allow manual fallback if user typed something without selecting from dropdown
    if (transferData.pickupType === 'airport' && !transferData.pickupAirportCode) {
      // Airport mode selected but no airport ID - this shouldn't happen with proper selection
      toast.error('Please select a valid airport from the list', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // Allow all entries - backend will geocode if coordinates are missing
    // This enables manual text entry without requiring Google Places selection
    // Validation for custom type with missing coords removed - backend handles geocoding
    
    // Dropoff validation
    if (!transferData.dropoff || transferData.dropoff.trim().length < 3) {
      toast.error('Please enter a destination address', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // Date validation
    if (!isValidDate(transferData.date)) {
      toast.error('Please select a valid date', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // Time validation
    if (!isValidTime(transferData.time)) {
      toast.error('Please select a valid time', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // 60 minute rule validation
    const tripDateTime = new Date(`${transferData.date}T${transferData.time}`);
    const now = new Date();
    const minTime = new Date(now.getTime() + 60 * 60 * 1000);
    
    if (tripDateTime < minTime) {
      toast.error('Transfer must be scheduled at least 60 minutes in advance', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    // Round-trip return date validation
    if (tripType === 'roundtrip' && !isValidDate(transferData.returnDate)) {
      toast.error('Please select a valid return date', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateTransfer()) return;

    setIsLoading(true);

    // Final validation guard before API call
    if (!isValidTime(transferData.time)) {
      console.warn('[BookingForm] Invalid time blocked:', transferData.time);
      toast.error('Invalid time format. Please select a valid time.', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      setIsLoading(false);
      return;
    }

    if (!isValidDate(transferData.date)) {
      console.warn('[BookingForm] Invalid date blocked:', transferData.date);
      toast.error('Invalid date format. Please select a valid date.', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      setIsLoading(false);
      return;
    }

    try {
      // Build payload with explicit date/time strings (NO Date objects)
      const payload = {
        pickup: transferData.pickup,
        dropoff: transferData.dropoff,
        pickupLat: transferData.pickupLat,
        pickupLng: transferData.pickupLng,
        pickupType: transferData.pickupType || 'manual', // 'airport', 'custom', 'manual'
        pickupAirportCode: transferData.pickupAirportCode,
        dropoffLat: transferData.dropoffLat,
        dropoffLng: transferData.dropoffLng,
        // Send date and time as plain strings - NEVER convert to Date object
        date: transferData.date,  // YYYY-MM-DD format
        time: transferData.time,  // HH:mm format
        timezone: 'Europe/Istanbul',  // Always use Turkey timezone for transfers
        passengers: parseInt(transferData.passengers),
        luggage: parseInt(transferData.luggage),
        tripType: tripType === 'roundtrip' ? 'round-trip' : 'one-way',
        tripMode: 'transfer',
        locale: 'en',
        currency: 'EUR',
      };

      if (tripType === 'roundtrip') {
        payload.returnDate = transferData.returnDate;
        payload.returnTime = transferData.returnTime || transferData.time;
      }

      // Debug log to verify what we're sending
      console.log('[BookingForm] Submitting payload:', {
        date: payload.date,
        time: payload.time,
        timezone: payload.timezone
      });

      const result = await apiPost('/api/booking/start', payload);

      if (result && result.success && result.sessionId) {
        // Always use the NEW sessionId from response - don't reuse old ones
        localStorage.setItem('vipgez_session', result.sessionId);
        localStorage.setItem('vipgez_booking_state', JSON.stringify({
          ...transferData,
          tripType,
          sessionId: result.sessionId,
        }));
        
        // Navigate with the new session ID
        navigate(`/quote?sid=${result.sessionId}`);
      } else {
        const errorMessage = result.error || 'An error occurred. Please try again.';
        toast.error(errorMessage, {
          style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
        });
      }
    } catch (error) {
      console.error('Booking start error:', error);
      toast.error('Connection error. Please try again.', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="booking-card border-0" data-testid="booking-form-card">
      <CardContent className="p-5 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-card-foreground">Book Your Transfer</h3>
          <p className="text-sm text-muted-foreground">Airport transfer service</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
          {/* Trip Type Toggle */}
          <div className="flex gap-2 p-1 bg-muted/30 rounded-lg">
            <button
              type="button"
              onClick={() => setTripType('oneway')}
              data-testid="trip-type-oneway"
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                tripType === 'oneway'
                  ? 'bg-card text-card-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              One-way
            </button>
            <button
              type="button"
              onClick={() => setTripType('roundtrip')}
              data-testid="trip-type-roundtrip"
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                tripType === 'roundtrip'
                  ? 'bg-card text-card-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              Round-trip
            </button>
          </div>

          {/* Pickup Location - Hybrid Input (Airport + Google Places) */}
          <div className="space-y-1.5">
            <Label className="text-card-foreground font-medium text-xs">From *</Label>
            <HybridLocationInput
              airports={airports}
              value={transferData.pickup}
              onChange={handlePickupChange}
              placeholder="Select airport or enter address..."
              testId="pickup-hybrid"
              iconType="pickup"
              prioritizeAirports={true}
            />
          </div>

          {/* Drop-off Location - Google Autocomplete */}
          <div className="space-y-1.5">
            <Label className="text-card-foreground font-medium text-xs">Destination Address *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
              <Input
                ref={dropoffRef}
                name="dropoff"
                placeholder="Enter hotel, address or location..."
                value={transferData.dropoff}
                onChange={handleDropoffInputChange}
                data-testid="dropoff-input"
                className="pl-10 h-11 bg-muted/30 border-border text-card-foreground placeholder:text-muted-foreground/60 text-sm"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Date & Time Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-card-foreground font-medium text-xs">Date *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                <Input
                  name="date"
                  type="date"
                  value={getSafeDateValue(transferData.date)}
                  onChange={handleTransferChange}
                  data-testid="date-input"
                  className="pl-10 h-11 bg-muted/30 border-border text-card-foreground text-sm cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-card-foreground font-medium text-xs">Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                <Input
                  name="time"
                  type="time"
                  value={getSafeTimeValue(transferData.time)}
                  onChange={handleTransferChange}
                  data-testid="time-input"
                  className="pl-10 h-11 bg-muted/30 border-border text-card-foreground text-sm cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                />
              </div>
            </div>
          </div>

          {/* Return Date & Time (only for round-trip) */}
          {tripType === 'roundtrip' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-card-foreground font-medium text-xs">Return Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary pointer-events-none z-10" />
                  <Input
                    name="returnDate"
                    type="date"
                    value={getSafeDateValue(transferData.returnDate)}
                    onChange={handleTransferChange}
                    data-testid="return-date-input"
                    className="pl-10 h-11 bg-muted/30 border-border text-card-foreground text-sm cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                    min={getSafeDateValue(transferData.date) || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground font-medium text-xs">Return Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary pointer-events-none z-10" />
                  <Input
                    name="returnTime"
                    type="time"
                    value={getSafeTimeValue(transferData.returnTime)}
                    onChange={handleTransferChange}
                    data-testid="return-time-input"
                    className="pl-10 h-11 bg-muted/30 border-border text-card-foreground text-sm cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Passengers & Luggage Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Passengers */}
            <div className="space-y-1.5">
              <Label className="text-card-foreground font-medium text-xs">Passengers</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <select
                  name="passengers"
                  value={transferData.passengers}
                  onChange={handleTransferChange}
                  data-testid="passengers-select"
                  className="w-full pl-10 h-11 bg-muted/30 border border-border rounded-lg text-card-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none appearance-none cursor-pointer"
                >
                  {[...Array(50)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Luggage Stepper */}
            <div className="space-y-1.5">
              <Label className="text-card-foreground font-medium text-xs">Luggage</Label>
              <div className="flex items-center h-11 bg-muted/30 border border-border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => handleLuggageChange(-1)}
                  data-testid="luggage-minus"
                  className="h-full px-3 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors active:scale-95"
                  disabled={parseInt(transferData.luggage) <= 0}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="flex-1 flex items-center justify-center gap-1.5">
                  <Luggage className="h-4 w-4 text-primary" />
                  <span className="text-card-foreground font-medium text-sm min-w-[24px] text-center" data-testid="luggage-value">
                    {transferData.luggage}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleLuggageChange(1)}
                  data-testid="luggage-plus"
                  className="h-full px-3 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors active:scale-95"
                  disabled={parseInt(transferData.luggage) >= 50}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="ctaLarge"
            size="lg"
            className="w-full mt-4 h-12"
            disabled={isLoading}
            data-testid="get-price-button"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-5 w-5 border-2 border-foreground border-t-transparent rounded-full"></span>
                Loading...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Get Price
                <ArrowRight className="h-5 w-5" />
              </span>
            )}
          </Button>

          {/* Payment Methods - Clean, no duplicate trust messages */}
          <div className="pt-3 border-t border-border/30 mt-3">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>Secure payment via</span>
              <span className="font-semibold text-card-foreground">PayPal</span>
              <span>•</span>
              <span className="font-semibold text-card-foreground">Card</span>
              <span>•</span>
              <span className="font-semibold text-card-foreground">Cash</span>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default BookingForm;
